import type { FC, MouseEventHandler } from 'react';
import { Typography } from '@mui/material';

import { PriceGroupBadge } from '../../atoms/PriceGroupBadge';
import { StatusPill } from '../../atoms/StatusPill';
import type { TStatusPillVariant } from '../../atoms/StatusPill/types';

import {
	StyledCard,
	StyledHeader,
	StyledLogo,
	StyledLogoFallback,
	StyledPriceRow,
	StyledTitleGroup,
	StyledValiditySection,
} from './MplTicketshopCard.styled';

export interface IMplTicketshopCardStatus {
	text: string;
	variant: TStatusPillVariant;
}

export interface IMplTicketshopCardProps {
	/**
	 * Operator logo URL provided by the backend. When omitted, a neutral
	 * placeholder circle is rendered.
	 */
	operatorIconUrl?: string;
	ticketName: string;
	operatorName: string;
	/**
	 * Pre-formatted validity lines, e.g.
	 * ['Gültig vom 19.05.2026', 'bis 20.05.2026, 12:00', 'Zone A'] for standard
	 * tickets, or ['Ab Hauptbahnhof', 'Gültig bis 12:00:00'] for Kurzstrecke.
	 * Composed by `mapMyTicketToCardProps`.
	 */
	validityLines: string[];
	priceGroupName?: string;
	/** Localized label for the price row, e.g. 'Gesamtpreis'. */
	priceLabel: string;
	/** Formatted price value, e.g. '2,00 €'. */
	priceValue: string;
	status?: IMplTicketshopCardStatus;
	onClick?: MouseEventHandler;
	dataTestId?: string;
}

export const MplTicketshopCard: FC<IMplTicketshopCardProps> = ({
	operatorIconUrl,
	ticketName,
	operatorName,
	validityLines,
	priceGroupName,
	priceLabel,
	priceValue,
	status,
	onClick,
	dataTestId,
}) => {
	return (
		<StyledCard
			onClick={onClick}
			data-testid={dataTestId}
			sx={{ cursor: onClick ? 'pointer' : 'default' }}
		>
			<StyledHeader>
				{operatorIconUrl ? (
					<StyledLogo src={operatorIconUrl} alt="" />
				) : (
					<StyledLogoFallback aria-hidden />
				)}
				<StyledTitleGroup>
					<Typography variant="b1-sb" color="primary.main">
						{ticketName}
					</Typography>
					<Typography variant="l1" color="primary.main">
						{operatorName}
					</Typography>
				</StyledTitleGroup>
				{status && <StatusPill text={status.text} variant={status.variant} />}
			</StyledHeader>

			<StyledValiditySection>
				{validityLines.map((line) => (
					<Typography key={line} variant="b2-m" color="primary.main">
						{line}
					</Typography>
				))}
				<PriceGroupBadge
					priceGroupName={priceGroupName}
					dataTestId="MplTicketshopCard-price-group"
				/>
			</StyledValiditySection>

			<StyledPriceRow>
				<Typography variant="h5-sb" color="primary.main">
					{priceLabel}
				</Typography>
				<Typography variant="b1-m" color="primary.main">
					{priceValue}
				</Typography>
			</StyledPriceRow>
		</StyledCard>
	);
};
